/** @jsx jsx */

import { useState, useContext, useEffect, useRef } from 'react';
import { css, jsx } from '@emotion/core';
import { interactionContext } from './interactionContext';
import { SelectOptionsContext } from './SelectOptionsRoot';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { State } from 'store/store';
import EditMarker from 'components/UI/EditMarker';
import { Tick } from 'components/Editor/EditorUtilities/UI/CheckBox';

const SelectOption = ({ children, ...props }) => {
  const iContext = useContext(interactionContext);

  const {
    dropDownTextColor = '#1D2026',
    dropDownBorder = true,
    dropDownBorderColor = '#D1D5E7',
    dropDownBorderRadius = 12,
    dropDownBackgroundColor = 'transparent',
    selectGroupFocusColor,
    selectOptionsFocusColor,
  } = iContext.props;

  const selectOptionsContext = useContext(SelectOptionsContext);

  let { showCorrectAnswerIconColor } = iContext.props;
  if (showCorrectAnswerIconColor === undefined) {
    showCorrectAnswerIconColor = dropDownBorderColor;
  }

  const {
    selectedOptions,
    setSelectedOptions,
    result,
    showCorrectAnswer,
    correctAnswers,
    isLive,
  } = selectOptionsContext;

  const { id, type, index: selectGroupIndex, values } = props;

  const typeSettings = useSelector((state: State) => {
    return (state?.theme?.typeSettings ||
      {}) as typeof state.theme.typeSettings;
  });

  const menuIsOpen = useSelector((state: State) => {
    if (!window.dev()) {
      return undefined;
    }
    return state.focusedNodeId === id;
  });

  const value = values[selectedOptions[selectGroupIndex]];

  const [txt] = children;
  const nameId = `i${id}`;

  const resolved = useRef(false);
  if (isLive === false && result === 'incorrect' && !resolved.current) {
    resolved.current = true;
  }

  const _ = ({ innerRef, innerProps, ...props }) => {
    const { label, isFocused, data } = props;
    const { index } = data;

    return (
      <div
        ref={innerRef}
        {...innerProps}
        css={css({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          lineHeight: '1.1em',
          padding: '5px 10px',
          fontSize,
          cursor: 'pointer',
          ...(selectOptionsFocusColor
            ? {
                color: isFocused ? `${selectOptionsFocusColor} !important` : '',
              }
            : {
                opacity: `${isFocused ? 0.5 : 1}`,
              }),
        })}
      >
        {label}
        {window.dev() && correctAnswers[selectGroupIndex] === index ? (
          <div
            css={css({
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 20,
              height: 20,
              padding: '0 2px 0 0',
              borderRadius: '50%',
              background: showCorrectAnswerIconColor,
              backgroundSize: 20,
            })}
          >
            <Tick />
          </div>
        ) : null}
      </div>
    );
  };

  const [menuIsOpenOnEnter, setMenuIsOpenOnEnter] = useState(false);

  const fontSize =
    typeSettings?.elementStyles?.p?.fontSize || typeSettings?.fontSize;

  return (
    <div
      id={nameId}
      css={css({
        position: 'relative',
        margin: '5px 0',
      })}
      onClick={
        window.dev()
          ? e => {
              setCurrentBlock(menuIsOpen ? null : { id });
            }
          : undefined
      }
    >
      <div
        css={css({
          flex: 1,
        })}
      >
        {txt}
      </div>

      <div style={{ height: 10 }} />

      <div
        css={css({
          position: 'relative',

          '[class*="control"]': {
            '*': {
              ':focus': {
                border: '0px !important',
              },
            },

            fontSize,
          },

          '*': {
            color: `${dropDownTextColor} !important`,

            ...(menuIsOpen && {
              '[class*="MenuList"]': {
                '*': {
                  pointerEvents: 'none',
                },
              },
            }),
          },
        })}
      >
        {/* @ts-ignore */}
        <Select
          key={value}
          menuIsOpen={menuIsOpen}
          isSearchable={false}
          className={'dropdown-select-group'}
          styles={{
            control: (base, state) => {
              const { isFocused } = state;

              ['borderColor', 'boxShadow', '&:hover'].forEach(entry => {
                delete base[entry];
              });

              return {
                ...base,
                background: dropDownBackgroundColor,
                minHeight: 45,
                border: dropDownBorder
                  ? `1px solid ${dropDownBorderColor}`
                  : 'none',
                borderRadius: dropDownBorderRadius,
                cursor: 'pointer',
                ...(!window.dev() &&
                  isFocused && {
                    boxShadow: `0 0 0 2px ${selectGroupFocusColor ||
                      dropDownBorderColor}`,
                  }),

                '[class*="placeholder"]': {
                  width: '97.5%',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                },

                '[class*="indicatorSeparator"]': {
                  display: 'none',
                },
              };
            },
            menu: (base, state) => {
              return {
                ...base,
                borderRadius: 10,
                boxShadow: `0 23px 36px rgba(0, 0, 0, 0.2)`,
                background:
                  dropDownBackgroundColor === 'transparent'
                    ? '#fff'
                    : dropDownBackgroundColor,
                '*': {
                  ':hover': {
                    ...(selectOptionsFocusColor
                      ? { color: `${selectOptionsFocusColor} !important` }
                      : {
                          //
                        }),
                  },
                },
              };
            },
          }}
          placeholder={value}
          value={value}
          onChange={({ value }) => {
            setSelectedOptions(selectGroupIndex, values.indexOf(value));
          }}
          options={[
            ...values.map((value, index) => {
              return { value, label: value, index };
            }),
          ]}
          components={{ Option: _ }}
        />

        {!!(!window.dev() && resolved.current && showCorrectAnswer) && (
          <div
            css={css({
              position: 'absolute',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              right: 10,
              top: 12.5,
              width: 20,
              height: 20,
              padding: '0 2px 0 0',
              borderRadius: '50%',
              background: showCorrectAnswerIconColor,
              backgroundSize: 20,
            })}
          >
            <Tick />
          </div>
        )}

        {window.dev() && (
          <div
            css={css({
              position: 'absolute',
              right: 0,
              top: 0,
              width: '100%',
              height: '100%',
            })}
          >
            <div
              css={css({
                position: 'absolute',
                left: -2,
                top: -2,
                width: 'calc(100% + 4px)',
                height: 'calc(100% + 4px)',
                boxShadow: menuIsOpen
                  ? '0 0 0 1px #ff0063'
                  : '0 0 0 1px lightGrey',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s',
                ':hover': {
                  background: 'rgba(0,0,0,0.02)',
                },
              })}
            >
              {!menuIsOpen && <EditMarker />}
            </div>
          </div>
        )}
      </div>

      <div style={{ height: 20 }} />
    </div>
  );
};

export default SelectOption;
